import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { api, API, formatApiErrorDetail } from "@/lib/api";

// Sales renders on a quote: the customer's stone shown in their room type.
export const RoomMockups = ({ quoteId, stone }) => {
  const [cfg, setCfg] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [room, setRoom] = useState("kitchen");
  const [busy, setBusy] = useState(false);
  const [shots, setShots] = useState([]);

  const load = useCallback(() => {
    if (!quoteId) return;
    api
      .get("/ai-images/history", { params: { quote_id: quoteId } })
      .then(({ data }) => setShots(data.items || []))
      .catch(() => setShots([]));
  }, [quoteId]);

  useEffect(() => {
    api
      .get("/settings/ai-images")
      .then(({ data }) => {
        setCfg(data.config);
        const list = (data.config.rooms || []).filter((r) => r.key);
        setRooms(list);
        setRoom(list[0]?.key || "kitchen");
      })
      .catch(() => setCfg({ enabled: false }));
    load();
  }, [load]);

  if (!cfg?.enabled || !cfg?.staff_tools || !quoteId) return null;

  const generate = async () => {
    setBusy(true);
    try {
      await api.post("/ai-images/generate", {
        room,
        stone: stone || "",
        purpose: "quote",
        quote_id: quoteId,
      });
      toast.success("Mockup added to this quote's files");
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not generate that mockup",
      );
    }
    setBusy(false);
  };

  return (
    <section
      className="no-print mb-4 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
      data-testid="quote-room-mockups"
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-zinc-200">Room mockup</div>
          <div className="mt-0.5 text-xs text-zinc-500">
            {stone
              ? `A render of ${stone} in the customer's room — saved to this quote's files`
              : "Pick a stone on the Project step first for the best likeness"}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {rooms.map((r) => (
            <button
              key={r.key}
              type="button"
              data-testid={`mockup-room-${r.key}`}
              onClick={() => setRoom(r.key)}
              className={`h-9 rounded-lg px-3 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                room === r.key
                  ? "bg-zinc-100 text-zinc-900"
                  : "border border-zinc-800 bg-zinc-950 text-zinc-400 hover:text-zinc-100"
              }`}
            >
              {r.label}
            </button>
          ))}
          <Button
            data-testid="mockup-generate"
            disabled={busy}
            onClick={generate}
            className="h-9 bg-indigo-500 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
          >
            <Sparkle size={13} weight="bold" className="mr-2" />
            {busy ? "Painting…" : "Generate"}
          </Button>
        </div>
      </div>

      {busy && (
        <div
          className="mt-4 h-40 animate-pulse rounded-lg border border-zinc-800 bg-zinc-950"
          data-testid="mockup-loading"
        />
      )}

      {shots.length > 0 && (
        <div
          className="mt-4 grid gap-3 sm:grid-cols-3 xl:grid-cols-4"
          data-testid="mockup-list"
        >
          {shots.map((s) => (
            <a
              key={s.path}
              href={`${API}/ai-images/image/${s.path}`}
              target="_blank"
              rel="noreferrer"
              data-testid={`mockup-shot-${s.path.split("/").pop()}`}
              className="group overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950"
            >
              <img
                src={`${API}/ai-images/image/${s.path}`}
                alt={s.room}
                className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <span className="block px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                {s.room || "Room"}
              </span>
            </a>
          ))}
        </div>
      )}
    </section>
  );
};

export default RoomMockups;
